/**
 * UpsellBanner — reusable consumer-mode CTA.
 *
 * Surfaces inside the prom flow ("you've experienced this; want
 * your own?") and routes to /create. Tracks `upsell_viewed` on
 * mount and `upsell_clicked` on tap so we can attribute conversions
 * back to the surface that triggered them.
 *
 * Variants:
 *   • "live"  — gradient hero card (used on the projector reveal screen)
 *   • "card"  — quiet inline card (challenges & ready pages)
 *   • "toast" — slide-up bottom toast (after share)
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Confetti, ArrowRight, Cake } from '@phosphor-icons/react';
import { trackGrowth } from '../lib/growthTrack';

export default function UpsellBanner({ source, variant = 'card', headline, sub, cta = 'Create my event' }) {
  const navigate = useNavigate();

  useEffect(() => {
    trackGrowth('upsell_viewed', { metadata: { source, variant } });
    // Run once per source/variant combination
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClick = () => {
    trackGrowth('upsell_clicked', { metadata: { source, variant } });
    navigate(`/start?from=${encodeURIComponent(source || 'unknown')}`);
  };

  const defaultHeadline = headline || 'Planning your own party?';
  const defaultSub = sub || 'Birthdays, house parties, uni nights — create yours in 30 seconds.';

  if (variant === 'live') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        className="rounded-3xl p-6 sm:p-7 mt-10 max-w-xl mx-auto text-center"
        style={{ background: 'linear-gradient(135deg, rgba(184,151,98,0.18), rgba(194,157,82,0.18))', border: '1px solid rgba(255,255,255,0.12)' }}
        data-testid={`upsell-banner-${variant}`}
      >
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}>
          <Confetti size={22} weight="fill" className="text-white" />
        </div>
        <p className="text-2xl sm:text-3xl font-black mb-1">{defaultHeadline}</p>
        <p className="text-white/65 text-sm sm:text-base mb-5">{defaultSub}</p>
        <button
          onClick={onClick}
          className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm font-black text-black bg-white hover:bg-neutral-200 transition-colors shadow-xl"
          data-testid={`upsell-cta-${variant}`}
        >
          {cta} <ArrowRight size={14} weight="bold" />
        </button>
      </motion.div>
    );
  }

  if (variant === 'toast') {
    return (
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        className="fixed inset-x-3 bottom-3 z-[90] rounded-2xl p-4 flex items-center gap-3 shadow-2xl"
        style={{ background: 'linear-gradient(135deg, #1a1c33, #0F1014)', border: '1px solid rgba(255,255,255,0.12)' }}
        data-testid={`upsell-banner-${variant}`}
      >
        <div className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}>
          <Cake size={18} weight="fill" className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-black text-white truncate">Want your own version of this?</p>
          <p className="text-[12px] text-white/55">Create your event in 30 seconds</p>
        </div>
        <button
          onClick={onClick}
          className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-lg text-[12px] font-black text-black bg-white hover:bg-neutral-200 transition-colors"
          data-testid={`upsell-cta-${variant}`}
        >
          Create <ArrowRight size={11} weight="bold" />
        </button>
      </motion.div>
    );
  }

  // Default: quiet inline card
  return (
    <div
      className="rounded-2xl p-5 mt-8 border"
      style={{ background: 'linear-gradient(135deg, rgba(184,151,98,0.08), rgba(194,157,82,0.08))', borderColor: 'rgba(255,255,255,0.10)' }}
      data-testid={`upsell-banner-${variant}`}
    >
      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#C29D52] mb-2 flex items-center gap-1.5">
        <Cake size={11} weight="fill" /> Make your own version
      </p>
      <p className="text-base font-black text-white mb-1">{defaultHeadline}</p>
      <p className="text-white/55 text-[13px] mb-3">{defaultSub}</p>
      <button
        onClick={onClick}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-[13px] font-black text-black bg-white hover:bg-neutral-200 transition-colors"
        data-testid={`upsell-cta-${variant}`}
      >
        {cta} <ArrowRight size={12} weight="bold" />
      </button>
    </div>
  );
}
